import {
  useAlternateLocales,
  useHostName,
  useLegacyConfig,
} from '@aboutyou/config';
import {useRequest} from '@aboutyou/data-fetcher';
import {Element} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/page.types';
import {GetSectionPageMetaDataResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/help_center/v1/meta/service.types';

import {useTranslation} from '@aboutyou/i18n';
import {GetArticlePageMetaDataResponse, JsonLd} from '../types';
import {
  ArticleFromIRIS,
  SectionsFromIRIS,
  fetchDataFromIRIS,
} from '../utils/iris/iris';
import {useArticleIdFromUrl} from './useArticleIdFromUrl';
import {useSectionIdFromUrl} from './useSectionIdFromUrl';

const META_DESCRIPTION_MAXIMUM_CHAR_LENGTH = 155;

function useGenerateMetaElements() {
  const hostname = useHostName();
  const alternateLocales = useAlternateLocales() ?? [];

  return ({path, description}: {path: string; description: string}) => {
    const metas: Element[] = [
      {
        type: {
          $case: 'meta',
          meta: {
            content: 'index,follow',
            type: {
              $case: 'name',
              name: 'robots',
            },
          },
        },
      },
      {
        type: {
          $case: 'meta',
          meta: {
            content: description,
            type: {
              $case: 'name',
              name: 'description',
            },
          },
        },
      },
    ];

    const links: Element[] = [
      {
        type: {
          $case: 'link',
          link: {
            rel: 'canonical',
            href: `https://${hostname}${path}`,
          },
        },
      },
    ];

    for (const locale of alternateLocales) {
      links.push({
        type: {
          $case: 'link',
          link: {
            rel: 'alternate',
            href: `${locale.url}${path}`,
            hreflang: locale.locale,
          },
        },
      });
    }

    return [...metas, ...links];
  };
}

function getHtmlStringText(htmlString: string): string {
  return htmlString.replace(/<\/?[^>]+(>|$)/g, '');
  /**
   * Alternatively, but won't work if we use SSR since document will be undefined:
   * const div = document.createElement("div");
   * div.innerHTML = htmlString;
   * return div.innerText;
   */
}

export function useGetMetaData() {
  const legacyConfig = useLegacyConfig();
  const {t} = useTranslation();

  const generateMetaElements = useGenerateMetaElements();
  const sectionIdFromUrl = useSectionIdFromUrl();
  const sectionId = sectionIdFromUrl === 0 ? undefined : sectionIdFromUrl;
  const articleId = useArticleIdFromUrl();

  return useRequest<
    GetSectionPageMetaDataResponse | GetArticlePageMetaDataResponse
  >(`HELP_CENTER_META_DATA-${articleId ?? sectionId ?? 'ROOT'}`, async () => {
    let title: string;
    let description: string;
    let path: string;
    let jsonLd: JsonLd | undefined;

    if (articleId != null && sectionId != null) {
      const {article} = await fetchDataFromIRIS<{article: ArticleFromIRIS}>(
        `/articles/${articleId}`,
        legacyConfig.help_center_GET,
        {noScope: true},
      );

      // Required for JSON LD and meta description
      const answer = getHtmlStringText(article.body);

      title = article.name;
      description =
        answer.substring(0, META_DESCRIPTION_MAXIMUM_CHAR_LENGTH) +
        (answer.length > META_DESCRIPTION_MAXIMUM_CHAR_LENGTH ? '...' : ''); // Ellipsise if required
      path = `/h/ssf/${sectionId}/q-${articleId}`;
      jsonLd = {entities: [{name: article.name, answer}]};
    } else if (sectionId != null) {
      const {sections} = await fetchDataFromIRIS<{sections: SectionsFromIRIS}>(
        '/sections',
        legacyConfig.help_center_GET,
      );
      const section = sections.find(section => section.id === sectionId)!;

      title = t('Questions about: {title}', {title: section.name});
      description = t(
        'Find answers to many questions, such as your delivery, payments, or personal settings.',
      );
      path = `/h/ssf/${section.id}`;
    } else {
      title = t('What topic do you need help with?');
      description = t(
        'Find answers to many questions, such as your delivery, payments, or personal settings.',
      );
      path = `/h/ssf/0`;
    }

    return {
      meta: {
        type: {
          $case: 'seoData',
          seoData: {
            title,
            elements: generateMetaElements({path, description}),
            hiddenNavigationLinks: [],
            jsonLd: [],
          },
        },
      },
      jsonLd,
    };
  });
}
